/**
 * Organization payout onboarding — corporate bank account form.
 *
 * The route is linked from the org payout dashboard. Only the org's
 * fleet_manager can create accounts, and the Edge Function enforces that too.
 *
 * ANTES NO HABIA NINGUNA COMPUERTA ACA, y el comentario decia que la Edge
 * Function lo comprobaba "de nuevo" -- pero no habia un primer chequeo que
 * repetir. Cualquiera con la URL y un `?orgId=` de una organizacion verificada
 * veia el formulario de cuenta bancaria ENTERO, con la razon social y el NIT de
 * esa empresa precargados, y podia llenarlo hasta el final para que el servidor
 * lo rechazara al enviar.
 *
 * No habia fuga de datos que RLS no permitiera --la politica
 * `organizations_select_verified` deja leer cualquier organizacion verificada--
 * pero mostrarle a alguien un formulario operable que nunca puede funcionar es
 * el callejon sin salida que este repo lleva meses sacando. Se dice lo que pasa
 * y se ofrece el siguiente paso, como en `hosting/payouts/onboarding.tsx`.
 */
import { useCallback } from 'react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { PayoutAccountForm } from '@/components/hosting/PayoutAccountForm'
import { EmptyState } from '@/components/ui/EmptyState'
import { useMyOrganizations, useOrganization, useOrgMembership } from '@/lib/hooks/use-organization'
import { useCreatePayoutAccountForOrg } from '@/lib/hooks/use-payouts'
import { SPACING } from '@/lib/theme/tokens'
import { getSvBankLabel } from '@/lib/data/sv-banks'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import type { PayoutAccountFormValues } from '@/lib/validations/payout.schema'

export default function OrgPayoutOnboardingScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { orgId: orgIdParam } = useLocalSearchParams<{ orgId: string }>()
  /*
   * SIN `?orgId=` ESTA PANTALLA SE QUEDABA EN BLANCO PARA SIEMPRE.
   *
   * La compuerta era `{!resolved ? null : ...}` y `resolved` exige
   * `query.isSuccess`. Con el parametro ausente la consulta queda
   * `enabled: false` y NUNCA llega a isSuccess: encabezado dibujado y cuerpo
   * vacio, sin esqueleto, sin mensaje y sin nada que tocar. Lo mismo con un
   * administrador legitimo cuya red se cayo.
   *
   * `resolved` colapsaba "todavia no se" y "no se pudo" en el mismo false. Son
   * hechos distintos y ahora se tratan distinto.
   */
  const { organizations, isLoading: isResolvingOrgs } = useMyOrganizations()
  const orgId = orgIdParam || organizations[0]?.id
  const { organization } = useOrganization(orgId)
  const { resolved, isMember, isManager, isLoading: membershipLoading } = useOrgMembership(orgId)
  const createAccount = useCreatePayoutAccountForOrg(orgId)

  const organizationName = organization?.legalName ?? organization?.name ?? ''

  const handleSubmit = useCallback(
    async (values: PayoutAccountFormValues) => {
      if (!orgId) return
      try {
        await createAccount.mutateAsync({
          accountType: values.accountType,
          bankName: getSvBankLabel(values.bankCode),
          bankCode: values.bankCode,
          accountNumber: values.accountNumber,
          accountHolderName: values.accountHolderName,
          documentNumber: values.documentNumber,
          countryCode: 'SV',
        })
        void notification(NotificationType.Success)
        toast.success(t('payouts.onboarding_success'))
        router.replace(`/hosting/organization/payouts?orgId=${orgId}` as never)
      } catch (err) {
        void notification(NotificationType.Error)
        toast.error(errorMessage(err, t('payouts.onboarding_failed')))
        captureError(err, { screen: 'org/payouts/onboarding', action: 'create_account' }, { orgId })
      }
    },
    [createAccount, orgId, router, t],
  )

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader
        title={t('org_payouts.onboarding_title')}
        subtitle={t('org_payouts.onboarding_subtitle')}
        backTestID="org-payouts.onboarding-back"
      />

      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING['3xl'],
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/*
          Mientras el servidor no conteste, `resolved` es false y no se afirma
          NADA: ni formulario ni muro. Es la misma forma que
          `hosting/payouts/onboarding.tsx` usa con `canReceivePayouts`, y falla
          hacia el lado seguro -- a un administrador legitimo con la red lenta se
          le hace esperar un instante, nunca se le dice que no pertenece.
        */}
        {isResolvingOrgs || membershipLoading ? null : !resolved ? (
          /*
            NO SE PUDO PREGUNTAR. Ni formulario ni acusacion: se dice que no se
            pudo cargar y se ofrece a donde ir. Antes esto era `null`, o sea un
            cuerpo en blanco permanente -- el peor de los tres estados, porque
            no dice nada y no se puede tocar.
          */
          <EmptyState
            unavailable
            testID="org-payouts.unreachable"
            title={t('common.error_title')}
            description={t('common.error_description')}
            action={{
              label: t('org.not_member_action'),
              onPress: () => router.replace('/(tabs)/hosting/organization' as never),
            }}
          />
        ) : !isMember ? (
          <EmptyState
            testID="org-payouts.not-member"
            title={t('org.not_member_title')}
            description={t('org.not_member_body')}
            action={{
              label: t('org.not_member_action'),
              onPress: () => router.replace('/(tabs)/hosting/organization' as never),
            }}
          />
        ) : !isManager ? (
          <EmptyState
            testID="org-payouts.not-manager"
            title={t('org.not_manager_title')}
            description={t('org.not_manager_body')}
            action={{
              label: t('org.not_manager_action'),
              onPress: () =>
                router.replace(`/hosting/organization/members?orgId=${orgId}` as never),
            }}
          />
        ) : (
          <PayoutAccountForm
            mode="organization"
            initialHolderName={organizationName}
            initialDocumentNumber={organization?.taxId ?? ''}
            organizationName={organizationName}
            submitting={createAccount.isPending}
            submitLabel={t('payouts.onboarding_submit')}
            testIDPrefix="org-payouts"
            onSubmit={handleSubmit}
          />
        )}
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
